import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EditEmployerData,
  Employee,
  RegisterEmployerData,
  UseEmployeesParams,
  UseEmployeesResponse,
} from './interfaces';
import { edupassClient, falconClient } from '@/data/@clients';
import { api_employees_path, reset_password_api_employee_path } from '@/utils/routes';

export const useEmployees = ({ searchParams }: UseEmployeesParams) => {
  const response = useQuery<{ data: UseEmployeesResponse }>({
    queryKey: ['employees', searchParams],
    queryFn: () =>
      edupassClient.get(`/employees?${new URLSearchParams(searchParams)}`),
    placeholderData: (previousData) => previousData,
  });

  return {
    employees: response.data?.data?.employees,
    companies: response.data?.data?.empresas,
    ...response,
  };
};

export const useResetEmployeePassword = () => {
  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: number;
    }): Promise<{ message: string }> =>
      (await falconClient.post(
        reset_password_api_employee_path({ id })
      )) as unknown as { message: string },
  });
};

export const useRemoveEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      edupassClient.delete(`/employees/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
};

export const useEditEmployeeEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) =>
      edupassClient.post(`/employees/${id}/change-email`, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
};

export const useRegisterEmployer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      data,
    }: {
      data: RegisterEmployerData;
    }): Promise<{ message: string }> =>
      (await falconClient.post(api_employees_path(), {
        employee: data,
      })) as unknown as { message: string },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
};

export const useEmployeeShow = ({ id }: { id: number }) => {
  const response = useQuery<{
    data: { employee: Employee[]; isAdmin: boolean };
  }>({
    queryKey: ['employees', id],
    queryFn: () => edupassClient.get(`/employees/${id}`),
  });
  return {
    employee: response.data?.data?.employee[0],
    isAdmin: response.data?.data?.isAdmin,
    ...response,
  };
};

export const useEditEmployer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: EditEmployerData }) =>
      edupassClient.put(`/employees/${id}`, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['employees'] });
    },
  });
};
